import { mdiMagnify } from "@mdi/js";
import Icon from "@mdi/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import DataTable from "components/DataTable";
import Input from "components/Input";
import { RefLoadingSpinner } from "components/LoadingSpinner";
import Select from "components/Select";
import {
  MY_CARRIERS_TABLE_HEADER_LIST,
  ORIGIN_CARRIER_SEARCH_FILTER_LIST,
  ORIGIN_CARRIER_STATUS_FILTER_LIST,
  PERCENTAGE_LIST
} from "constants/haulpay-broker";
import { routes } from "constants/routes";
import MyCarrierItem from "features/main/HaulPay/modules/Broker/MyCarrierItem";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { IOption } from "models/Form";
import { IFactoringPaymentProfile, IMyCarrierFilterOption } from "models/HaulPay";
import { getFactoringPaymentProfilesAPI, updateCarrierProfileAPI } from "services/api/haulpay";
import { selectFactoringProfile, selectUserDetails } from "store/user/selector";
import CarrierInfoModal from "./CarrierInfoModal";
import DefaultSettingsModal from "./DefaultSettingModal";
import styles from "./style.module.scss";
import { useBrokerContext } from "context/BrokerContext";

export interface ICarriersFormData {
  carrier_id: string;
  carrier_rate: string[];
  external_code: string[];
  payment_speed: (IOption & {
    amount: number;
    discountRate?: number;
    rate?: number;
  })[];
  broker_portion: IOption[];
}

export default function MyCarriers() {
  const navigate = useNavigate();
  const userDetails = useSelector(selectUserDetails);
  const factoringProfile = useSelector(selectFactoringProfile);
  const searchModalRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useDeviceType();

  const [isSearchBarExpanded, setIsSearchBarExpanded] = useState(false);

  const [selectedCarrier, setSelectedCarrier] = useState<IFactoringPaymentProfile>();

  const [isDefaultSettingModalOpen, toggleDefaultSettingModalOpen] = useToggle(false);
  const [isCarrierInfoModalOpen, toggleCarrierInfoModal] = useToggle();
  const { defaultPaymentSettings, getDefaultPaymentSettings } = useBrokerContext();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { isSubmitting }
  } = useForm<IMyCarrierFilterOption>({
    defaultValues: {
      filter_text: "",
      filter_type: "carrier_dba",
      filter_status: "pending"
    }
  });

  const fetchMyCarriers = useCallback(
    async (factoringId: string, params: any) => {
      const filterData = getValues();
      return (
        factoringId &&
        getFactoringPaymentProfilesAPI(factoringId, filterData, params.limit, params.offset)
      );
    },
    [userDetails?.factoring_id]
  );

  const {
    data: myCarriers,
    loading: isLoadingMore,
    paginationInfo,
    observerRef,
    goToPage,
    loadNextPage,
    loadPreviousPage,
    setPageSizeFn,
    resetPagination
  } = useInfiniteScroll<IFactoringPaymentProfile>(
    (params: any) => fetchMyCarriers(userDetails.factoring_id, params),
    {
      threshold: 0.9,
      concatData: true
    },
    [userDetails?.factoring_id]
  );
  const methods = useForm<Partial<ICarriersFormData>>();
  const { getValues: getCarriersListValue, reset: resetCarriersForm } = methods;

  const filterType = watch("filter_type");

  function goToAddNew() {
    navigate(routes.HAULPAY_BROKER_CARRIERS_NEW);
  }

  const onFilterCarriers = () => {
    resetPagination();
  };

  function buildPaymentSpeeds(apiObj: any) {
    const profiles = Array.isArray(apiObj) ? apiObj : Object.values(apiObj);

    return profiles.map((p, idx) => ({
      id: idx,
      label: `${p.name} (${p.discount_rate}% Quick Pay Fee)`,
      value: p.value ?? `ach_${p.payout_days === 0 ? 1 : p.payout_days}_day`,
      day: p.payout_days,
      amount: p.discount_rate
    }));
  }

  const setMyCarriersData = (carriers: IFactoringPaymentProfile[]) => {
    resetCarriersForm({
      external_code: carriers.map((carrier) => carrier.external_code),
      payment_speed: carriers.map((carrier) => {
        const paymentTerms = buildPaymentSpeeds(carrier.payment_speed);
        return paymentTerms.find((speed) => speed.value === carrier.payout_days);
      }),
      broker_portion: carriers.map((carrier) =>
        PERCENTAGE_LIST.find(
          (percent) => Number(percent.value) === carrier.factoring_fee_split_rate
        )
      ),
      carrier_rate: carriers.map((carrier) => {
        const paymentTerm = carrier?.payment_speed?.find((term) => {
          const payoutDays = term.payout_days === 0 ? 1 : term.payout_days;
          return payoutDays == Number(carrier.payout_days?.match(/-?\d+(\.\d+)?/g)?.[0]);
        });
        if (paymentTerm) {
          return (
            (paymentTerm?.discount_rate * (100 - carrier?.factoring_fee_split_rate)) / 100 + "%"
          );
        }
        return "0%";
      })
    });
    setIsSearchBarExpanded(false);
  };

  const onSaveCarrier = async (index: number, id: string) => {
    const info = getCarriersListValue([
      `payment_speed.${index}`,
      `broker_portion.${index}`,
      `carrier_rate.${index}`,
      `external_code.${index}`
    ]);
    try {
      await updateCarrierProfileAPI({
        factoring_id: userDetails.factoring_id,
        carrier_id: id,
        send_update_email: factoringProfile.send_rate_confirmation_pdf,
        data: {
          enable_discount: myCarriers.find((carrier) => carrier.id === id)?.enable_discount,
          external_code: info[3],
          factoring_fee_split_rate: Number(info[1].value),
          payout_days: info[0].value
        }
      });
      showSuccessMessage("Carrier has been updated.");
    } catch (err: any) {
      showErrorMessage(err.message);
    }
  };

  const handleToggleInfoModal = (id: string) => {
    setSelectedCarrier(myCarriers.find((carrier) => carrier.id === id));
    toggleCarrierInfoModal();
  };

  const pendingTasks = (
    needs_account_info: boolean,
    needs_factor_info: boolean,
    needs_noa: boolean,
    needs_verified_identity: boolean
  ) => {
    const tasks = [];
    if (needs_account_info) {
      tasks.push("Need Acct. info");
    }
    if (needs_factor_info) {
      tasks.push("Need Factor info");
    }
    if (needs_noa) {
      tasks.push("Need NOA");
    }
    if (needs_verified_identity) {
      tasks.push("Verifying Bank Acct or NOA");
    }
    return tasks;
  };

  const handleOutsideInputClick = (e: any) => {
    if (!searchModalRef.current?.contains(e.target as Node)) {
      setIsSearchBarExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideInputClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideInputClick);
    };
  }, []);

  useEffect(() => {
    setMyCarriersData(myCarriers);
  }, [myCarriers]);

  useEffect(() => {
    if (userDetails?.factoring_id && !defaultPaymentSettings.length) {
      getDefaultPaymentSettings(userDetails?.factoring_id);
    }
  }, [userDetails?.factoring_id, defaultPaymentSettings.length]);

  const columns = useMemo(
    () =>
      MY_CARRIERS_TABLE_HEADER_LIST(
        myCarriers,
        onSaveCarrier,
        handleToggleInfoModal,
        defaultPaymentSettings,
        methods.register,
        methods.control,
        methods.getValues,
        methods.setValue
      ),
    [defaultPaymentSettings, myCarriers]
  );

  return (
    <div className={styles.originCarrierContainer}>
      <div className={styles.body}>
        <form className="relative flex-1" onSubmit={handleSubmit(onFilterCarriers)}>
          <Input
            name="filter_text"
            prefixNode={
              <Icon
                path={mdiMagnify}
                className={clsx(styles.searchButton, { "z-40": isSearchBarExpanded })}
              />
            }
            placeholder="Search for"
            register={register}
            inputClass={clsx(
              styles.searchWrapper,
              { "!bg-card": !isDesktop && !isSearchBarExpanded },
              { "z-30": isSearchBarExpanded }
            )}
            onFocus={() => setIsSearchBarExpanded(true)}
            autoComplete="off"
          />
          {isSearchBarExpanded && (
            <>
              <div className="fixed inset-0 z-20 bg-bgColor/90" />
              <div ref={searchModalRef} className={styles.searchModal}>
                <div className="flex gap-4 mobile:flex-col">
                  <Controller
                    name="filter_type"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        options={ORIGIN_CARRIER_SEARCH_FILTER_LIST}
                        value={ORIGIN_CARRIER_SEARCH_FILTER_LIST.find(
                          (filter_type) => filter_type.value === value
                        )}
                        onChange={(item) => {
                          onChange(item);
                          setValue("filter_type", item.value);
                        }}
                        btnClass="!py-3 !pl-3 desktop:!pr-12 !rounded-lg text-sm"
                        dropdownClass="h-auto"
                        dropdownContainerClass="max-h-auto"
                        className="w-full desktop:w-40"
                        arrowClass="!right-1"
                      />
                    )}
                  />

                  {filterType === "status" && (
                    <Controller
                      name="filter_status"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          options={ORIGIN_CARRIER_STATUS_FILTER_LIST}
                          value={ORIGIN_CARRIER_STATUS_FILTER_LIST.find(
                            (filter_status) => filter_status.value === value
                          )}
                          onChange={(item) => {
                            onChange(item);
                            setValue("filter_status", item.value);
                          }}
                          btnClass="!py-3 !pl-3 desktop:!pr-12 !rounded-lg text-sm h-11"
                          dropdownContainerClass="max-h-auto"
                          className="w-44 mobile:w-full"
                          arrowClass="!right-1"
                        />
                      )}
                    />
                  )}
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  rounded
                  className="self-end h-10 text-sm w-fit"
                  loading={isSubmitting}>
                  Search
                </Button>
              </div>
            </>
          )}
        </form>

        <div className="flex gap-2 mobile:w-full mobile:justify-between">
          <Button
            rounded
            variant="outline"
            className="desktop:!h-12 desktop:!px-9 desktop:!rounded-base desktop:order-1 order-2"
            onClick={toggleDefaultSettingModalOpen}>
            Defaults
          </Button>
          <Button
            rounded
            className="desktop:!h-12 mobile:text-xs desktop:!px-9 !px-5 desktop:!rounded-base desktop:order-2 order-1"
            onClick={goToAddNew}>
            {isDesktop ? "Add New" : "ADD NEW"}
          </Button>
        </div>
      </div>

      {isDesktop ? (
        <form>
          <DataTable
            className={styles.desktopCarrierList}
            headers={columns || []}
            data={myCarriers.map((item) => ({
              ...item,
              status: {
                carrierStatus: item.payment_profile.status,
                statusDate: item.status_date,
                pendingApprovalTasks: pendingTasks(
                  item.payment_profile.needs_account_info,
                  item.payment_profile.needs_factor_info,
                  item.payment_profile.needs_noa,
                  item.payment_profile.needs_verified_identity
                )
              }
            }))}
            totalCount={paginationInfo.totalCount}
            nextLink={paginationInfo.hasNextPage}
            prevLink={paginationInfo.hasPreviousPage}
            setPageSize={setPageSizeFn}
            goToPage={goToPage}
            fetchPrevPage={loadPreviousPage}
            fetchNextPage={loadNextPage}
            loading={isLoadingMore}
            infiniteScroll
          />
        </form>
      ) : (
        <div className={styles.carrierList}>
          {myCarriers.map((item) => (
            <MyCarrierItem
              key={item.id}
              data={item}
              send_update_email={factoringProfile.send_rate_confirmation_pdf}
              toggleInfoModal={handleToggleInfoModal}
              pendingApprovalTasks={pendingTasks(
                item.payment_profile.needs_account_info,
                item.payment_profile.needs_factor_info,
                item.payment_profile.needs_noa,
                item.payment_profile.needs_verified_identity
              )}
              defaultPaymentSettings={defaultPaymentSettings}
            />
          ))}
          <RefLoadingSpinner observerRef={observerRef} isLoadingMore={isLoadingMore} />
        </div>
      )}
      {isDefaultSettingModalOpen && (
        <DefaultSettingsModal
          isOpen={isDefaultSettingModalOpen}
          closeModal={toggleDefaultSettingModalOpen}
        />
      )}

      {myCarriers.length ? (
        <CarrierInfoModal
          isOpen={isCarrierInfoModalOpen}
          closeModal={() => toggleCarrierInfoModal()}
          carrierInfo={selectedCarrier ?? myCarriers[0]}
        />
      ) : null}
    </div>
  );
}
